import { useState } from 'react';
import {
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface NotificationSetting {
  id: string;
  type: string;
  description: string;
  enabled: boolean;
}

interface AlertThreshold {
  id: string;
  metric: string;
  threshold: number;
  period: string;
  enabled: boolean;
}

interface Webhook {
  id: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive';
  lastDelivery?: string;
}

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

const mockNotificationSettings: NotificationSetting[] = [
  {
    id: '1',
    type: 'API Key Created',
    description: 'Receive notifications when new API keys are generated',
    enabled: true,
  },
  {
    id: '2',
    type: 'Usage Limits',
    description: 'Get alerts when approaching API usage limits',
    enabled: true,
  },
  {
    id: '3',
    type: 'Security Events',
    description: 'Be notified of important security events',
    enabled: true,
  },
  {
    id: '4',
    type: 'Billing Updates',
    description: 'Receive updates about billing and payments',
    enabled: false,
  },
];

const mockAlertThresholds: AlertThreshold[] = [
  {
    id: '1',
    metric: 'API Calls',
    threshold: 80,
    period: 'daily',
    enabled: true,
  },
  {
    id: '2',
    metric: 'Error Rate',
    threshold: 5,
    period: 'hourly',
    enabled: true,
  },
  {
    id: '3',
    metric: 'Response Time',
    threshold: 500,
    period: 'realtime',
    enabled: false,
  },
];

const mockWebhooks: Webhook[] = [
  {
    id: '1',
    url: 'https://api.example.com/webhooks/events',
    events: ['api.key.created', 'api.key.revoked'],
    status: 'active',
    lastDelivery: '2024-01-20 15:30:00',
  },
  {
    id: '2',
    url: 'https://api.example.com/webhooks/usage',
    events: ['usage.limit.approaching', 'usage.limit.exceeded'],
    status: 'inactive',
  },
];

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'warning',
    title: 'API Usage Alert',
    message: 'You have reached 80% of your daily API call limit',
    timestamp: '2024-01-20 15:30:00',
    read: false,
  },
  {
    id: '2',
    type: 'success',
    title: 'New API Key Generated',
    message: 'A new API key has been created for Project Alpha',
    timestamp: '2024-01-20 14:15:00',
    read: false,
  },
  {
    id: '3',
    type: 'error',
    title: 'Webhook Delivery Failed',
    message: 'Failed to deliver webhook to endpoint: Connection timeout',
    timestamp: '2024-01-20 12:45:00',
    read: true,
  },
];

export default function Notifications() {
  const [settings, setSettings] = useState(mockNotificationSettings);
  const [thresholds, setThresholds] = useState(mockAlertThresholds);
  const [webhooks, setWebhooks] = useState(mockWebhooks);
  const [notifications, setNotifications] = useState(mockNotifications);

  const toggleSetting = (id: string) => {
    setSettings(settings.map(setting =>
      setting.id === id ? { ...setting, enabled: !setting.enabled } : setting
    ));
  };

  const toggleThreshold = (id: string) => {
    setThresholds(thresholds.map(threshold =>
      threshold.id === id ? { ...threshold, enabled: !threshold.enabled } : threshold
    ));
  };

  const toggleWebhook = (id: string) => {
    setWebhooks(webhooks.map(webhook =>
      webhook.id === id ? {
        ...webhook,
        status: webhook.status === 'active' ? 'inactive' : 'active'
      } : webhook
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(notification => ({
      ...notification,
      read: true
    })));
  };

  return (
    <div className="space-y-8">
      {/* Notifications Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <BellIcon className="h-6 w-6 text-white/90" />
          <h1 className="text-xl font-medium text-white">Notifications</h1>
        </div>

        {/* Email Notification Settings */}
        <div className="p-8 space-y-6">
          <h2 className="text-lg font-medium text-white">Email Notifications</h2>
          <div className="space-y-4">
            {settings.map((setting) => (
              <div
                key={setting.id}
                className="flex items-center justify-between p-4 glass-panel-dark"
              >
                <div>
                  <div className="font-medium text-white">{setting.type}</div>
                  <div className="text-sm text-white/50">{setting.description}</div>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => toggleSetting(setting.id)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      setting.enabled ? 'bg-magick-purple' : 'bg-white/10'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        setting.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alert Thresholds */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Alert Thresholds</h2>
        </div>
        <div className="p-8 space-y-4">
          {thresholds.map((threshold) => (
            <div
              key={threshold.id}
              className="flex items-center justify-between p-4 glass-panel-dark"
            >
              <div>
                <div className="font-medium text-white">{threshold.metric}</div>
                <div className="text-sm text-white/50">
                  Alert when {threshold.metric.toLowerCase()} exceeds {threshold.threshold}%
                  ({threshold.period} check)
                </div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => toggleThreshold(threshold.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    threshold.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      threshold.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Webhook Configuration */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">Webhook Configuration</h2>
          <button className="btn-primary">Add Webhook</button>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="p-8">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="font-medium text-white break-all">{webhook.url}</div>
                  <div className="text-sm text-white/50">
                    Events: {webhook.events.join(', ')}
                  </div>
                  {webhook.lastDelivery && (
                    <div className="text-sm text-white/30">
                      Last delivery: {webhook.lastDelivery}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      webhook.status === 'active'
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-rose-500/10 text-rose-300'
                    }`}
                  >
                    {webhook.status}
                  </span>
                  <button
                    onClick={() => toggleWebhook(webhook.id)}
                    className="btn-secondary text-sm"
                  >
                    {webhook.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Notifications */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">Recent Notifications</h2>
          <button
            onClick={markAllAsRead}
            className="btn-secondary text-sm"
          >
            Mark All as Read
          </button>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`px-8 py-4 ${
                !notification.read ? 'bg-white/[0.02]' : ''
              }`}
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`p-2 rounded ${
                    notification.type === 'success'
                      ? 'bg-emerald-500/10'
                      : notification.type === 'warning'
                      ? 'bg-amber-500/10'
                      : 'bg-rose-500/10'
                  }`}
                >
                  {notification.type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-300" />
                  ) : notification.type === 'warning' ? (
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-300" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-rose-300" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-white">
                      {notification.title}
                    </div>
                    <div className="text-sm text-white/30">
                      {notification.timestamp}
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-white/70">
                    {notification.message}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
